import { useEffect } from 'react';
import { getRoute, useNavigate } from '../../../../routes';
import { useGetSurveyStatus } from '../../core/hooks/useGetSurveyStatus';

export type UseSurveyStartReturn =
  | { loading: true }
  | {
      loading: false;
      surveyName: string;
      estimatedCompletionMinutes: string;
    };

/**
 * Hook that prepares data for the Survey Start page.
 * Responsibilities:
 *  - Fetch current survey status
 *  - Redirect to survey home when status is not Incomplete or on error
 *  - Expose loading state while fetching or while a redirect is occurring
 *  - Provide survey metadata (name & estimated time) once eligible
 */
export const useSurveyStart = (): UseSurveyStartReturn => {
  const navigate = useNavigate();
  const surveyStatusResult = useGetSurveyStatus();

  useEffect(() => {
    if (surveyStatusResult.loading) {
      return;
    }

    if (!surveyStatusResult.success) {
      // On error, redirect to survey home to allow central error handling UI
      navigate(getRoute('surveyHome', {}), { replace: true });
      return;
    }

    const { survey } = surveyStatusResult;

    // If user does not have an Incomplete survey, redirect to survey home
    if (survey.status !== 'Incomplete') {
      navigate(getRoute('surveyHome', {}), { replace: true });
    }
  }, [navigate, surveyStatusResult]);

  if (surveyStatusResult.loading) {
    return { loading: true };
  }

  if (!surveyStatusResult.success) {
    return { loading: true }; // error branch triggers redirect in useEffect
  }

  if (surveyStatusResult.survey.status !== 'Incomplete') {
    return { loading: true }; // non-incomplete branch also redirects in useEffect
  }

  return {
    loading: false,
    surveyName: surveyStatusResult.survey.name,
    estimatedCompletionMinutes: surveyStatusResult.survey.estimatedCompletionMinutes,
  };
};
